import { useCallback, useEffect, useState } from 'react';
import { Card, PageHeader, Stat, StatusBadge, Toggle, FormField, FormSelect, TextInput } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi, type CostsOverview, type CostRate, type CostRow, type EngineRow, type OpenRouterKeySource, type PlatformOpenRouterKey } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * Agorot → "₪x.xx" (עד 4 ספרות אחרי הנקודה — תעריף של שברי אגורה כמו 2.5
 * חייב להציג ₪0.025 ולא להתעגל ל-₪0.03). הסימן תמיד לפני המספר כדי שהקריאה
 * ב-RTL לא תתהפך.
 */
function shekels(agorot: number): string {
  return `₪${(agorot / 100).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/** עלות AI אמיתית מהספק, בדולרים (usage.cost). */
function usd(dollars: number): string {
  return `$${(dollars ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/**
 * Platform cost/margin analysis for super-admin: what each tenant costs us
 * (telephony, AI, storage…) vs what we bill them, per month. Rates are editable
 * inline (in agorot); the per-tenant table expands to show the cost breakdown.
 */
export function SuperCosts() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState<CostsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await superApi.costs(month));
    } catch {
      toast('שגיאה בטעינת ניתוח העלויות', 'danger');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { void load(); }, [load]);

  function patchRate(next: CostRate) {
    setData((d) => (d ? { ...d, rates: d.rates.map((r) => (r.key === next.key ? next : r)) } : d));
  }
  function addRate(next: CostRate) {
    setData((d) => (d ? { ...d, rates: [...d.rates, next] } : d));
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="עלויות מערכת" actions={
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ padding: 8, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' }}
          aria-label="בחירת חודש"
        />
      } />

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
          <Stat label="עלות כוללת (הערכה)" value={shekels(data.totals.cost_agorot)} />
          <Stat label="עלות AI בפועל" value={usd(data.totals.actual_ai_cost_usd)} />
          <Stat label="הכנסה כוללת" value={shekels(data.totals.revenue_agorot)} />
          <Stat label="מרווח" value={shekels(data.totals.margin_agorot)} />
        </div>
      )}

      <PlatformKeyCard onChanged={load} />

      <RatesCard rates={data?.rates ?? []} onPatch={patchRate} onAdd={addRate} />

      <Card padded={false}>
        {loading ? (
          <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)' }}>טוען…</div>
        ) : !data || data.rows.length === 0 ? (
          <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)' }}>אין מרכזיות.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['', 'מרכזיה', 'סטטוס', 'מפתח OpenRouter', 'עלות (הערכה)', 'עלות AI בפועל', 'הכנסה', 'מרווח'].map((h, i) => (
                  <th key={i} style={{ padding: '10px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => <TenantCostRow key={r.tenant_id} row={r} onKeyChanged={load} />)}
              <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--surface-2)' }}>
                <td />
                <td style={{ padding: '10px 12px', fontWeight: 700 }} colSpan={3}>סה״כ</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{shekels(data.totals.cost_agorot)}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{usd(data.totals.actual_ai_cost_usd)}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700 }} dir="ltr">{shekels(data.totals.revenue_agorot)}</td>
                <td style={{ padding: '10px 12px', fontWeight: 800, color: data.totals.margin_agorot >= 0 ? 'var(--ok)' : 'var(--danger)' }} dir="ltr">{shekels(data.totals.margin_agorot)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        עלות = סכום צריכת המשאבים לפי התעריפים למעלה. הכנסה = החיוב בפועל מהמרכזיה. סכומים באגורות, מוצגים בשקלים.
      </p>
      <EnginesCard />
    </div>
  );
}

function RatesCard({ rates, onPatch, onAdd }: { rates: CostRate[]; onPatch: (r: CostRate) => void; onAdd: (r: CostRate) => void }) {
  const [adding, setAdding] = useState(false);
  return (
    <Card title="תעריפים">
      <div className="stack">
        {rates.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין עדיין תעריפים.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {rates.map((r) => <RateRow key={r.key} rate={r} onPatch={onPatch} />)}
          </div>
        )}
        {adding ? (
          <AddRateForm onAdded={(r) => { onAdd(r); setAdding(false); }} onCancel={() => setAdding(false)} />
        ) : (
          <div>
            <button type="button" className="btn" onClick={() => setAdding(true)}>+ הוסף תעריף</button>
          </div>
        )}
      </div>
    </Card>
  );
}

function RateRow({ rate, onPatch }: { rate: CostRate; onPatch: (r: CostRate) => void }) {
  const [value, setValue] = useState(String(rate.rate_agorot));
  const [saving, setSaving] = useState(false);
  const dirty = value !== String(rate.rate_agorot);

  async function save(patch: { rate_agorot?: number; enabled?: boolean }) {
    setSaving(true);
    try {
      const next = await superApi.updateCostRate(rate.key, patch);
      onPatch(next);
      if (patch.rate_agorot !== undefined) setValue(String(next.rate_agorot));
      toast('התעריף עודכן', 'ok');
    } catch {
      toast('שגיאה בעדכון התעריף', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
      <div style={{ flex: 1, minWidth: 160 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{rate.label}</strong>
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{rate.unit}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <TextInput
          type="number"
          dir="ltr"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: 100 }}
          aria-label={`תעריף ${rate.label} באגורות`}
        />
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          אגורות = <span dir="ltr" style={{ fontWeight: 600 }}>{shekels(Number(value) || 0)}</span>
        </span>
      </div>
      {dirty && (
        <button type="button" className="btn primary" disabled={saving} onClick={() => save({ rate_agorot: Math.max(0, Number(value) || 0) })}>שמור</button>
      )}
      <Toggle checked={rate.enabled} onChange={(v) => save({ enabled: v })} label={rate.enabled ? 'פעיל' : 'כבוי'} disabled={saving} />
    </div>
  );
}

function AddRateForm({ onAdded, onCancel }: { onAdded: (r: CostRate) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ key: '', label: '', unit: 'minute', rate_agorot: '' });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.key || !form.label) { toast('יש למלא מפתח ותווית', 'warn'); return; }
    setSaving(true);
    try {
      const r = await superApi.createCostRate({
        key: form.key.trim(),
        label: form.label.trim(),
        unit: form.unit,
        rate_agorot: Math.max(0, Number(form.rate_agorot) || 0),
      });
      toast('התעריף נוסף', 'ok');
      onAdded(r);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בהוספת התעריף', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12, display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        <FormField label="מפתח"><TextInput dir="ltr" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="pstn_minute" /></FormField>
        <FormField label="תווית"><TextInput value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></FormField>
        <FormField label="יחידה">
          <FormSelect value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            <option value="minute">דקה</option>
            <option value="message">הודעה</option>
            <option value="gb">GB</option>
            <option value="user">משתמש</option>
            <option value="call">שיחה</option>
          </FormSelect>
        </FormField>
        <FormField label="תעריף (אגורות)"><TextInput type="number" dir="ltr" value={form.rate_agorot} onChange={(e) => setForm({ ...form, rate_agorot: e.target.value })} /></FormField>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={submit} disabled={saving}>{saving ? 'שומר…' : 'הוסף'}</button>
      </div>
    </div>
  );
}

function TenantCostRow({ row, onKeyChanged }: { row: CostRow; onKeyChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(false);
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--line)', cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <td style={{ padding: '10px 12px', width: 32 }}>
          <Icon name="chevronD" size={14} style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 120ms ease' }} />
        </td>
        <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }}>
          <strong>{row.tenant_name}</strong>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{row.tenant_slug}</div>
        </td>
        <td style={{ padding: '10px 12px' }}>
          <StatusBadge tone={row.status === 'active' ? 'ok' : row.status === 'trial' ? 'warn' : 'neutral'} dot size="sm">{row.status}</StatusBadge>
        </td>
        <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <KeySourceBadge source={row.openrouter_key_source} />
            <button
              type="button"
              className="btn"
              style={{ padding: '2px 8px', fontSize: 'var(--t-xs)' }}
              onClick={() => setEditingKey(true)}
            >
              {row.openrouter_key_source === 'personal' ? 'ערוך' : 'הגדר אישי'}
            </button>
          </div>
        </td>
        <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">{shekels(row.cost_agorot)}</td>
        <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr" title={`תמלול ${usd(row.actual_transcription_usd)} · סיכום ${usd(row.actual_summary_usd)}`}>
          {usd(row.actual_ai_cost_usd)}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 'var(--t-sm)' }} dir="ltr">
          {row.pricing_configured ? shekels(row.revenue_agorot) : <span style={{ color: 'var(--warn)', fontSize: 'var(--t-xs)' }}>לא הוגדר תמחור</span>}
        </td>
        <td style={{ padding: '10px 12px', fontWeight: 700, color: row.margin_agorot >= 0 ? 'var(--ok)' : 'var(--danger)' }} dir="ltr">{shekels(row.margin_agorot)}</td>
      </tr>
      {editingKey && (
        <tr style={{ background: 'var(--surface-2)' }}>
          <td />
          <td colSpan={7} style={{ padding: '8px 12px' }}>
            <TenantKeyEditor row={row} onClose={() => setEditingKey(false)} onSaved={() => { setEditingKey(false); onKeyChanged(); }} />
          </td>
        </tr>
      )}
      {open && (
        <tr style={{ background: 'var(--surface-2)' }}>
          <td />
          <td colSpan={7} style={{ padding: '8px 12px' }}>
            {row.lines.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>אין צריכה בחודש זה.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['רכיב', 'כמות', 'תעריף', 'עלות'].map((h) => (
                      <th key={h} style={{ padding: '4px 8px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {row.lines.map((l) => (
                    <tr key={l.key}>
                      <td style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }}>{l.label}</td>
                      <td style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }} dir="ltr">{l.quantity.toLocaleString('he-IL')} {l.unit}</td>
                      <td style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }} dir="ltr">{shekels(l.rate_agorot)}</td>
                      <td style={{ padding: '4px 8px', fontSize: 'var(--t-xs)' }} dir="ltr">{shekels(l.cost_agorot)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}


/** תג מקור המפתח שממנו המרכזיה תתמלל בפועל. */
function KeySourceBadge({ source }: { source: OpenRouterKeySource }) {
  if (source === 'personal') return <StatusBadge tone="ok" size="sm" dot>אישי</StatusBadge>;
  if (source === 'system') return <StatusBadge tone="neutral" size="sm" dot>כללי</StatusBadge>;
  return <StatusBadge tone="danger" size="sm" dot>אין מפתח</StatusBadge>;
}

/**
 * עריכת מפתח OpenRouter אישי למרכזיה. שדה ריק + שמירה = מחיקת המפתח האישי
 * (חזרה לכללי). המפתח נשלח דרך PATCH /super/tenants/{id} (openrouter_api_key),
 * נשמר מוצפן ואינו חוזר לעולם.
 */
function TenantKeyEditor({ row, onClose, onSaved }: { row: CostRow; onClose: () => void; onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  async function save(clear = false) {
    setSaving(true);
    try {
      await superApi.updateTenant(row.tenant_id, { openrouter_api_key: clear ? null : value.trim() });
      toast(clear ? 'המפתח האישי הוסר — חזרה למפתח הכללי' : 'המפתח האישי נשמר', 'ok');
      onSaved();
    } catch {
      toast('שגיאה בשמירת המפתח', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
      <FormField label={`מפתח OpenRouter אישי — ${row.tenant_name}`} hint="מוצפן; ריק ושמירה = מחיקה. גובר על המפתח הכללי.">
        <TextInput
          dir="ltr"
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="sk-or-v1-..."
          style={{ width: 320, maxWidth: '100%' }}
        />
      </FormField>
      <button type="button" className="btn primary" disabled={saving || !value.trim()} onClick={() => save(false)}>
        {saving ? 'שומר…' : 'שמור'}
      </button>
      {row.openrouter_key_source === 'personal' && (
        <button type="button" className="btn danger" disabled={saving} onClick={() => save(true)}>מחק אישי</button>
      )}
      <button type="button" className="btn" disabled={saving} onClick={onClose}>ביטול</button>
    </div>
  );
}

/**
 * ניהול המפתח הכללי (המערכתי) של OpenRouter — ברירת המחדל לכל מרכזיה שאין לה
 * מפתח אישי. נשמר מוצפן ב-platform_settings ולעולם לא נחשף (רק מסכה). כשמקורו
 * 'env' פירושו שהוא עדיין מגיע מ-MERKAZIYA_OPENROUTER_KEY ולא נוהל מהממשק.
 */
function PlatformKeyCard({ onChanged }: { onChanged: () => void }) {
  const [status, setStatus] = useState<PlatformOpenRouterKey | null>(null);
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    void superApi.platformKeys().then((d) => setStatus(d.openrouter)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save(clear = false) {
    setSaving(true);
    try {
      const next = await superApi.updatePlatformOpenRouterKey(clear ? null : value.trim());
      setStatus(next);
      setValue('');
      setEditing(false);
      toast(clear ? 'המפתח הכללי הוסר' : 'המפתח הכללי נשמר', 'ok');
      onChanged();
    } catch {
      toast('שגיאה בשמירת המפתח הכללי', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="מפתח OpenRouter כללי (ברירת מחדל לכל המרכזיות)">
      <div className="stack" style={{ gap: 10 }}>
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          המפתח הכללי משמש כל מרכזיה שאין לה מפתח אישי. בלעדיו — תמלול OpenRouter/Whisper ייכשל
          וסיכומי AI לא יופקו. מרכזיה יכולה לגבור עם מפתח אישי (בטבלה למטה).
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          {status?.configured ? (
            <>
              <StatusBadge tone="ok" size="sm" dot>מוגדר</StatusBadge>
              <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{status.masked}</span>
              <StatusBadge tone="neutral" size="sm">{status.source === 'managed' ? 'מנוהל מהממשק' : 'מ-env (מומלץ לנהל מכאן)'}</StatusBadge>
            </>
          ) : (
            <StatusBadge tone="danger" size="sm" dot>לא מוגדר — כל התמלולים ייכשלו</StatusBadge>
          )}
          <span style={{ flex: 1 }} />
          {!editing && (
            <button type="button" className="btn" onClick={() => setEditing(true)}>{status?.configured ? 'החלף מפתח' : 'הגדר מפתח'}</button>
          )}
          {!editing && status?.source === 'managed' && (
            <button type="button" className="btn danger" disabled={saving} onClick={() => save(true)}>מחק</button>
          )}
        </div>

        {editing && (
          <div style={{ display: 'flex', alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
            <FormField label="מפתח OpenRouter כללי" hint="מוצפן; לא נחשף שוב לאחר שמירה.">
              <TextInput
                dir="ltr"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="sk-or-v1-..."
                style={{ width: 360, maxWidth: '100%' }}
              />
            </FormField>
            <button type="button" className="btn primary" disabled={saving || !value.trim()} onClick={() => save(false)}>
              {saving ? 'שומר…' : 'שמור'}
            </button>
            <button type="button" className="btn" disabled={saving} onClick={() => { setEditing(false); setValue(''); }}>ביטול</button>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * ניהול מנועי התמלול: Gemini תמיד קיים כברירת מחדל וכגיבוי; מנועי OpenRouter
 * מתווספים כמועמדים, עוברים בדיקת איכות עברית (הקלטות אמיתיות מול Gemini),
 * ורק אחרי אישור נכנסים לרשימת הבחירה פר-מרכזיה.
 */
function EnginesCard() {
  const [engines, setEngines] = useState<EngineRow[]>([]);
  const [envModels, setEnvModels] = useState<string[]>([]);
  const [minOverlap, setMinOverlap] = useState(60);
  const [newModel, setNewModel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [benchKeywords, setBenchKeywords] = useState('ביטול,מחיר');
  const [busy, setBusy] = useState<number | 'add' | null>(null);

  const load = useCallback(() => {
    void superApi.engines().then((d) => {
      setEngines(d.engines);
      setEnvModels(d.env_models);
      setMinOverlap(d.min_overlap_pct);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!newModel.trim()) { toast('הזן מזהה מודל (OpenRouter)', 'danger'); return; }
    setBusy('add');
    try {
      await superApi.addEngine(newModel.trim(), newLabel.trim() || undefined);
      setNewModel(''); setNewLabel('');
      toast('המנוע נוסף כמועמד — הרץ עליו בדיקת איכות', 'ok');
      load();
    } catch (e) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בהוספה', 'danger');
    } finally { setBusy(null); }
  }

  async function bench(e: EngineRow) {
    setBusy(e.id);
    try {
      await superApi.benchEngine(e.id, { keywords: benchKeywords || undefined, limit: 5 });
      toast('בדיקת האיכות הסתיימה', 'ok');
      load();
    } catch (err) {
      toast((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'הבדיקה נכשלה', 'danger');
    } finally { setBusy(null); }
  }

  async function setStatus(e: EngineRow, status: 'approved' | 'disabled' | 'candidate', force = false) {
    setBusy(e.id);
    try {
      await superApi.updateEngine(e.id, { status, force });
      toast('עודכן', 'ok');
      load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה', 'danger');
    } finally { setBusy(null); }
  }

  return (
    <Card title="מנועי תמלול (מילות מפתח)">
      <div className="stack" style={{ gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          Gemini הוא תמיד ברירת המחדל והגיבוי של כל המרכזיות. מנוע OpenRouter נכנס לבחירה
          פר-מרכזיה רק אחרי בדיקת איכות עברית עוברת (חפיפה ≥ {minOverlap}% מול Gemini על הקלטות אמיתיות).
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'end' }}>
          <FormField label="מזהה מודל (OpenRouter)"><TextInput value={newModel} onChange={(ev) => setNewModel(ev.target.value)} dir="ltr" placeholder="openai/gpt-4o-mini-transcribe" /></FormField>
          <FormField label="כינוי"><TextInput value={newLabel} onChange={(ev) => setNewLabel(ev.target.value)} /></FormField>
          <button type="button" className="btn primary" onClick={add} disabled={busy === 'add'}>{busy === 'add' ? 'מוסיף…' : '+ הוסף מנוע'}</button>
        </div>

        <FormField label="מילות מפתח לבדיקה" hint="הבדיקה מוודאת שהמנוע מזהה אותן כמו Gemini.">
          <TextInput value={benchKeywords} onChange={(ev) => setBenchKeywords(ev.target.value)} placeholder="ביטול,מחיר" />
        </FormField>

        <div className="stack" style={{ gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
            <strong style={{ flex: 1 }}>Gemini</strong>
            <StatusBadge tone="ok" size="sm" dot>ברירת מחדל + גיבוי — תמיד פעיל</StatusBadge>
          </div>
          {envModels.map((m) => (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
              <span dir="ltr" style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{m}</span>
              <StatusBadge tone="neutral" size="sm">מאושר דרך env (נעול)</StatusBadge>
            </div>
          ))}
          {engines.map((e) => (
            <div key={e.id} style={{ padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{e.model_id}</span>
                {e.label && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{e.label}</span>}
                <StatusBadge tone={e.status === 'approved' ? 'ok' : e.status === 'disabled' ? 'danger' : 'warn'} size="sm" dot>
                  {e.status === 'approved' ? 'מאושר' : e.status === 'disabled' ? 'מושבת' : 'מועמד'}
                </StatusBadge>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn" disabled={busy === e.id} onClick={() => bench(e)}>
                  {busy === e.id ? 'בודק…' : 'בדוק איכות'}
                </button>
                {e.status !== 'approved' && (
                  <button type="button" className="btn primary" disabled={busy === e.id} onClick={() => setStatus(e, 'approved')}>אשר</button>
                )}
                {e.status === 'approved' && (
                  <button type="button" className="btn danger" disabled={busy === e.id} onClick={() => setStatus(e, 'disabled')}>השבת</button>
                )}
              </div>
              {e.bench && (
                <div style={{ fontSize: 'var(--t-xs)', color: e.bench.passed ? 'var(--ok)' : 'var(--danger)', marginTop: 4 }}>
                  {e.bench.passed ? '✓ עבר' : '✗ לא עבר'} · חפיפה {e.bench.avg_overlap_pct}%
                  {e.bench.keyword_agreement_pct != null && <> · מילות מפתח {e.bench.keyword_agreement_pct}%</>}
                  {' '}· {e.bench.sample_count} הקלטות · {Math.round(e.bench.avg_latency_ms / 1000)}s ממוצע
                  {e.benched_at && <> · נבדק {new Date(e.benched_at).toLocaleDateString('he-IL')}</>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
